import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { GenericCrudService } from 'src/app/services/crud/generic-crud.service';
import Swal from 'sweetalert2';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/app/environments/environments';
import * as moment from 'moment';
import { ProductionEquipment, ProductionLine, ProductionSpeciality } from 'src/app/models/interfaces.model';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';

@Component({
  selector: 'app-modal-new-binnacle',
  templateUrl: './modal-new-binnacle.component.html',
  styleUrls: ['./modal-new-binnacle.component.css'],
})
export class ModalNewBinnacleComponent {
  
  requestForm: FormGroup;
  loadingFull: boolean = true;
  loading: any;
  dateNow: string = '';
  requesterUser: string = '';
  photo: File | null = null;
  problem: string | null = null;

  productionLineSelected: string | null = null;
  productionEquipmentSelected: string | null = null;
  productionSpecialitySelected: string | null = null;

  productionLine: ProductionLine[] = [];
  productionEquipment: ProductionEquipment[] = [];
  productionSpeciality: ProductionSpeciality[] = [];

  constructor(
    private fb: FormBuilder,
    public thisModalRef: MdbModalRef<ModalNewBinnacleComponent> | null = null,
    private crudService: GenericCrudService<any>
  ) {
    this.requestForm = this.fb.group({
      productionLineSelected: [null, [this.validateSelectedOptions]],
      productionEquipmentSelected: [null, [this.validateSelectedOptions]],
      productionSpecialitySelected: [null, [this.validateSelectedOptions]],
      problem: [null, [this.validateSelectedOptions]],
      photo: [null],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      this.requestForm.get('productionEquipmentSelected')?.disable();
      this.dateNow = moment(new Date()).format('DD-MM-YYYY');
      this.loadingFull = true;
      await Promise.all([
        this.getRequesterUser(),
        this.getProductionLine(),
        this.getProductionSpeciality(),
      ]);
    } catch (error) {
      console.log('[ngOnInit] error: ', error);
    } finally {
      this.loadingFull = false;
    }
  }

  async getRequesterUser(): Promise<void> {
    const idUser = localStorage.getItem('IdUser') ?? '';
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'USERS');
    body.append('id', idUser);
    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if (response && response.status === 1 && response.data) {
        this.requesterUser = response.data[0].SAMACCOUNTNAME;
      }
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  async getProductionLine(): Promise<void> {
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'PRODUCTION_LINE');

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if (response && response.status === 1 && response.data) {
        this.productionLine = response.data;
      }
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  async getProductionEquipment(id_production_line: any): Promise<boolean> {
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'PRODUCTION_EQUIPMENT');
    body.append('id_production_line', id_production_line);

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GetProductionEquipments)
      );
      if (response && response.status === 1 && response.data) {
        this.productionEquipment = response.data;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  async getProductionSpeciality(): Promise<void> {
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'PRODUCTION_SPECIALITY');

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if (response && response.status === 1 && response.data) {
        this.productionSpeciality = response.data;
      }
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  async buildProductionEquipment(id_production_line: any): Promise<void> {
    try {
      this.loadingFull = true;
      this.requestForm.get('productionEquipmentSelected')?.disable();
      if (await this.getProductionEquipment(id_production_line)) {
        this.requestForm.get('productionEquipmentSelected')?.enable();
      }
    } catch (error) {
      console.log('error: ', error);
    } finally {
      this.loadingFull = false;
    }
  }

  CleanAndResetForm() {
    this.requestForm.reset();
  }

  validateSelectedOptions(control: FormControl) {
    console.log('control: ', control);
    const selectedOptions = control.value;
    return selectedOptions && selectedOptions.length > 0
      ? null
      : { required: true };
  }

  validatePhoto(control: FormControl) {
    const file = control.value;
    return file && file instanceof File ? null : { required: true };
  }

  async createRequestBinnacle(): Promise<void> {
    try {
      if (this.requestForm.invalid) {
        this.requestForm.markAllAsTouched();
        return;
      }
      Swal.fire({
        title:
          'Está a punto de enviar una nueva solicitud, ¿Está seguro de realizar esta acción?',
        icon: 'warning',
        width: 800,
        customClass: {
          popup: 'custom-swal-popup',
        },
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.loadingFull = true;
          const body = new FormData();
          body.append('action', 'INSERT');
          body.append('table_name', 'BINNACLE');
          body.append('id_creator_user', localStorage.getItem('IdUser') ?? '');
          body.append('id_company', localStorage.getItem('IdCompany') ?? '');
          body.append(
            'id_production_line',
            this.requestForm.value.productionLineSelected
          );
          body.append(
            'id_production_equipment',
            this.requestForm.value.productionEquipmentSelected
          );
          body.append(
            'id_production_speciality',
            this.requestForm.value.productionSpecialitySelected
          );
          body.append('problem', this.requestForm.value.problem);
          body.append('status', '1');
          body.append('active', '1');
          if (this.photo !== null) {
            body.append('photo', this.photo);
          }

          const response: any = await firstValueFrom(
            this.crudService.crud(body, environment.CreateBinnacle)
          );
          this.loadingFull = false;
          if (response.status === 1) {
            this.CleanAndResetForm();
            Swal.fire({
              icon: 'success',
              title: 'Registro de solicitud bitácora creada exitosamente',
              showConfirmButton: false,
              timer: 2000,
            });
            window.location.reload();
          }
        }
      });
    } catch (error) {
      this.loadingFull = false;
      console.log('[createRequestBinnacle] Error: ', error);
    }
  }

  onFileAdded(files: File[]): void {
    this.photo = files[0];
    this.requestForm.get('photo')?.setValue(this.photo);
  }

  onFileRemoved(): void {
    this.photo = null;
    this.requestForm.get('photo')?.setValue(null);
  }

  closeModal() {
    this.thisModalRef?.close(true);
  }
}
